// 2D front/back body built from the 3D avatars (assets-pipeline/build_body_svg.py), as a zoom flow:
// the overview shows muscle groups; tapping one zooms the camera into it, where single muscles can be
// picked (select mode) or inspected (heat mode). Done, a tap on empty space, ‹ Body, Escape or the
// system back gesture zoom out again. The camera is one CSS transform on the SVG's wrapper, animated
// with the Web Animations API (compositor-friendly, interruptible); reduced motion gets a crossfade.
import {
  forwardRef, useCallback, useEffect, useId, useImperativeHandle, useLayoutEffect, useRef, useState, type KeyboardEvent,
} from 'react';
import female from '../body/body_female.json';
import male from '../body/body_male.json';
import type { Avatar } from '../backend/types.ts';
import { group as groupInfo, groupOf, groups, muscle, musclesIn } from '../lib/catalog.ts';
import { relative } from '../lib/format.ts';
import type { LoadLevel, MuscleLoad } from '../lib/load.ts';
import { confirmGroup, doubleTap, groupState, isSelected, setGroup, type Selection } from '../lib/selection.ts';
import { cameraCss, cameraFor, OVERVIEW, sameCamera, ZOOM, type Box, type Camera } from '../lib/zoom.ts';
import { Segmented } from './components.tsx';

type Side = 'front' | 'back';
interface BodyView { outline: string; muscles: Record<string, string>; groups: Record<string, { path: string; box: number[] }> }
interface BodyData { viewBox: number[]; views: Record<Side, BodyView> }
const BODIES: Record<Avatar, BodyData> = { male: male as BodyData, female: female as BodyData };

export const HEAT: Record<LoadLevel, string> = {
  fresh: 'var(--heat-fresh)', light: 'var(--heat-light)', moderate: 'var(--heat-moderate)', high: 'var(--heat-high)',
};
const LEVEL_LABEL: Record<LoadLevel, string> = { fresh: 'Fresh', light: 'Light', moderate: 'Worked', high: 'Loaded' };
const LEVEL_RANK: Record<LoadLevel, number> = { fresh: 0, light: 1, moderate: 2, high: 3 };

type Mode =
  | { kind: 'heat'; loads: Map<string, MuscleLoad> }
  | { kind: 'select'; selection: Selection; onChange: (s: Selection) => void };

export interface BodyMapHandle {
  /** Confirm the zoomed group (nothing picked = whole group) and zoom out. */
  done(): void;
}

interface Props {
  avatar: Avatar;
  mode: Mode;
  /** Called with the zoomed group, or null on the overview. */
  onFocusChange?: (group: string | null) => void;
  /** The screen shows its own Done button (LogSession's footer); the panel then has none. */
  externalDone?: boolean;
}

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const buzz = () => { try { navigator.vibrate?.(8); } catch { /* not supported (iOS) */ } };
const activate = (fn: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); }
};

export const BodyMap = forwardRef<BodyMapHandle, Props>(function BodyMap({ avatar, mode, onFocusChange, externalDone }, ref) {
  const uid = useId().replace(/:/g, '');
  const body = BODIES[avatar];
  const [view, setView] = useState<Side>('front');
  const [focus, setFocus] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const touched = useRef(false);
  const pushed = useRef(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const cameraRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const anim = useRef<Animation | null>(null);
  const camera = useRef<Camera>(OVERVIEW);
  const moved = useRef({ focus: null as string | null, view: 'front' as Side });
  const [size, setSize] = useState({ width: 0, height: 0 });
  const blurRef = useRef<SVGFEGaussianBlurElement>(null);
  const data = body.views[view];
  const sel = mode.kind === 'select' ? mode.selection : null;

  const focusChange = useRef(onFocusChange);
  focusChange.current = onFocusChange;
  useEffect(() => { focusChange.current?.(focus); }, [focus]);

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => setSize({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Move the camera whenever the target changes: animated for zoom and side switches, instant for resizes.
  useLayoutEffect(() => {
    const el = cameraRef.current;
    if (!el || !size.width) return;
    const box = focus ? data.groups[focus]?.box : undefined;
    const target = box
      ? cameraFor(box as unknown as Box, body.viewBox as unknown as Box, { ...size, insetBottom: panelRef.current?.offsetHeight ?? 0 })
      : OVERVIEW;
    const navigated = moved.current.focus !== focus || moved.current.view !== view;
    moved.current = { focus, view };
    if (sameCamera(target, camera.current) && !navigated) return;
    // Interrupted mid-flight: start from where the camera is now, not from where it was heading.
    const from = anim.current?.playState === 'running' ? getComputedStyle(el).transform : cameraCss(camera.current);
    anim.current?.cancel();
    camera.current = target;
    el.style.transform = cameraCss(target);
    // Hairlines and glow are drawn in SVG units: divide by the scale so they stay thin when zoomed
    // (set directly, not through React state, so the zoom start costs no extra render).
    el.style.setProperty('--z', String(target.scale));
    blurRef.current?.setAttribute('stdDeviation', String(2.4 / target.scale));
    if (!navigated) return;
    if (reducedMotion()) {
      anim.current = el.animate([{ opacity: 0.25 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' });
      return;
    }
    anim.current = el.animate([{ transform: from === 'none' ? cameraCss(OVERVIEW) : from }, { transform: cameraCss(target) }],
      { duration: focus ? ZOOM.durationIn : ZOOM.durationOut, easing: ZOOM.easing });
  }, [focus, view, size, data, body.viewBox]);

  const leave = useCallback((confirm: boolean) => {
    if (!focus) return;
    if (confirm && sel && mode.kind === 'select' && !touched.current) {
      const next = confirmGroup(sel, focus);
      if (next !== sel) { mode.onChange(next); buzz(); }
    }
    const from = focus;
    setFocus(null);
    setPicked(null);
    if (pushed.current) { pushed.current = false; history.back(); }
    requestAnimationFrame(() => stageRef.current?.querySelector<SVGElement>(`[data-group="${from}"]`)?.focus({ preventScroll: true }));
  }, [focus, sel, mode]);

  useImperativeHandle(ref, () => ({ done: () => leave(true) }), [leave]);

  const enter = (g: string) => {
    // Open the group on the side that shows it best (the back seen from the front is a sliver of traps).
    const other: Side = view === 'front' ? 'back' : 'front';
    const area = (side: Side) => { const b = body.views[side].groups[g]?.box; return b ? b[2] * b[3] : 0; };
    if (!area(view) && !area(other)) return; // list-only muscle group: nothing to zoom to
    if (area(other) > 3 * area(view)) setView(other);
    touched.current = false;
    if (mode.kind === 'heat') {
      const top = musclesIn(g).map((m) => m.id).filter((id) => mode.loads.has(id))
        .sort((a, b) => LEVEL_RANK[mode.loads.get(b)!.level] - LEVEL_RANK[mode.loads.get(a)!.level])[0];
      setPicked(top ?? null);
    } else setPicked(null);
    setFocus(g);
    if (!pushed.current) { history.pushState({ recoverBodyZoom: true }, ''); pushed.current = true; }
    requestAnimationFrame(() => backRef.current?.focus({ preventScroll: true }));
  };

  // System back gesture / browser back = tap-out. Escape too (before the sheet's own Escape closes it).
  useEffect(() => {
    const onPop = () => { if (pushed.current) { pushed.current = false; setFocus(null); setPicked(null); } };
    addEventListener('popstate', onPop);
    return () => removeEventListener('popstate', onPop);
  }, []);
  useEffect(() => {
    if (!focus) return;
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); leave(false); } };
    addEventListener('keydown', onKey, true);
    return () => removeEventListener('keydown', onKey, true);
  }, [focus, leave]);
  useEffect(() => () => { if (pushed.current) { pushed.current = false; history.back(); } }, []);

  const switchSide = (side: Side) => {
    if (focus && !body.views[side].groups[focus]) leave(false);
    setView(side);
    if (!focus && !reducedMotion()) cameraRef.current?.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
  };

  const pick = (id: string) => {
    setPicked(id);
    if (mode.kind !== 'select') return;
    const g = groupOf(id)!;
    touched.current = true;
    // One-muscle groups (neck) have nothing to narrow: the muscle is the group.
    mode.onChange(musclesIn(g).length === 1 ? setGroup(mode.selection, g, groupState(mode.selection, g) !== 'whole') : doubleTap(mode.selection, id));
    buzz();
  };

  const toggleWhole = (g: string) => {
    if (mode.kind !== 'select') return;
    touched.current = true;
    setPicked(null);
    mode.onChange(setGroup(mode.selection, g, groupState(mode.selection, g) !== 'whole'));
    buzz();
  };

  const onStageClick = (e: React.MouseEvent) => {
    const t = e.target as Element;
    if (focus && !t.closest('[data-muscle], .bm-panel')) leave(false);
  };

  const fill = (id: string) => {
    if (mode.kind === 'heat') {
      const level = mode.loads.get(id)?.level;
      return level && level !== 'fresh' ? HEAT[level] : `url(#${uid}-shade)`;
    }
    return isSelected(mode.selection, id) ? 'var(--accent)' : `url(#${uid}-shade)`;
  };
  const glows = (id: string) => (mode.kind === 'heat' ? mode.loads.get(id)?.level === 'high' : isSelected(mode.selection, id));
  const groupLabel = (g: string) => {
    const name = groupInfo(g)?.name ?? g;
    if (mode.kind === 'heat') {
      const levels = musclesIn(g).map((m) => mode.loads.get(m.id)?.level ?? 'fresh');
      const top = levels.reduce((a, b) => (LEVEL_RANK[b] > LEVEL_RANK[a] ? b : a), 'fresh' as LoadLevel);
      return `${name}, ${LEVEL_LABEL[top].toLowerCase()}`;
    }
    const st = groupState(mode.selection, g);
    return `${name}, ${st === 'whole' ? 'whole group selected' : st === 'some' ? 'some muscles selected' : 'not selected'}`;
  };

  const focusName = focus ? groupInfo(focus)?.name ?? focus : '';
  const inFocus = (id: string) => !!focus && groupOf(id) === focus;

  return (
    <div className={`body-map${focus ? ' zoomed' : ''}`} data-view={view}>
      <div className="bm-bar">
        <nav className="bm-crumbs" aria-label="Body map">
          {focus ? (
            <>
              <button ref={backRef} type="button" className="bm-crumb-back" onClick={() => leave(false)} data-testid="bm-back"
                aria-label="Back to the whole body">‹ Body</button>
              <span className="bm-crumb-sep" aria-hidden="true">›</span>
              <span className="bm-crumb-here" aria-current="location" data-testid="bm-title">{focusName}</span>
            </>
          ) : <span className="bm-crumb-here" aria-current="location" data-testid="bm-title">Body</span>}
        </nav>
        <div className="bm-side">
          <Segmented label="Body side" options={[['front', 'Front'], ['back', 'Back']]} value={view} onChange={switchSide} />
        </div>
      </div>

      <div className="bm-stage" ref={stageRef} onClick={onStageClick} data-testid="bm-stage">
        <div className="bm-camera" ref={cameraRef}>
          <svg viewBox={body.viewBox.join(' ')} role="group" aria-label={focus ? `${focusName}, ${view} view` : `Body, ${view} view`}>
            <defs>
              <linearGradient id={`${uid}-shade`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="var(--muscle-hi)" />
                <stop offset="1" stopColor="var(--muscle)" />
              </linearGradient>
              <filter id={`${uid}-glow`} x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur ref={blurRef} stdDeviation="2.4" />
              </filter>
            </defs>
            <path d={data.outline} className="bm-outline" />
            <g className="bm-glow" filter={`url(#${uid}-glow)`} aria-hidden="true">
              {Object.entries(data.muscles).filter(([id]) => glows(id) && (!focus || inFocus(id))).map(([id, d]) => (
                <path key={id} d={d} fill={fill(id)} />
              ))}
            </g>
            <g className="bm-muscles">
              {Object.entries(data.muscles).map(([id, d]) => {
                const live = inFocus(id);
                const name = muscle(id)?.name ?? id;
                return (
                  <path key={id} d={d} data-muscle={id} fill={fill(id)}
                    className={`zone${focus ? (live ? ' live' : ' dim') : ''}`}
                    {...(live ? {
                      role: 'button', tabIndex: 0,
                      'aria-label': mode.kind === 'heat' ? `${name}, ${LEVEL_LABEL[mode.loads.get(id)?.level ?? 'fresh'].toLowerCase()}` : name,
                      'aria-pressed': mode.kind === 'select' ? isSelected(mode.selection, id) : undefined,
                      onClick: () => pick(id), onKeyDown: activate(() => pick(id)),
                    } : { 'aria-hidden': true })} />
                );
              })}
            </g>
            <g className="bm-groups">
              {Object.entries(data.groups).map(([g, { path }]) => {
                const st = sel ? groupState(sel, g) : 'none';
                return focus ? (
                  g === focus && <path key={g} d={path} className="bm-group current" aria-hidden="true" />
                ) : (
                  <path key={g} d={path} data-group={g} className={`bm-group tappable${st !== 'none' ? ` sel-${st}` : ''}`}
                    role="button" tabIndex={0} aria-label={groupLabel(g)}
                    onClick={() => enter(g)} onKeyDown={activate(() => enter(g))} />
                );
              })}
            </g>
          </svg>
        </div>
        {!focus && mode.kind === 'heat' && <p className="bm-hint" aria-hidden="true">Tap a group for details</p>}
        {focus && (
          <GroupPanel ref={panelRef} group={focus} mode={mode} picked={picked} onPick={pick} onWhole={toggleWhole}
            onDone={externalDone ? undefined : () => leave(mode.kind === 'select')} />
        )}
      </div>

      {mode.kind === 'select' && <SelectionChips selection={mode.selection} onOpen={enter} current={focus} />}
    </div>
  );
});

const GroupPanel = forwardRef<HTMLElement, {
  group: string; mode: Mode; picked: string | null;
  onPick: (id: string) => void; onWhole: (g: string) => void; onDone?: () => void;
}>(function GroupPanel({ group: g, mode, picked, onPick, onWhole, onDone }, ref) {
  const name = groupInfo(g)?.name ?? g;
  const list = musclesIn(g);
  const m = picked ? muscle(picked) : undefined;
  const st = mode.kind === 'select' ? groupState(mode.selection, g) : 'none';
  const load = m && mode.kind === 'heat' ? mode.loads.get(m.id) : undefined;
  return (
    <section ref={ref} className="bm-panel" aria-label={`${name} muscles`} data-testid="bm-panel">
      <div className="bm-info" aria-live="polite" data-testid="bm-info">
        {m ? (
          <>
            <p className="bm-info-title"><strong>{m.name}</strong>{m.anatomical !== m.name && <span className="bm-anat">{m.anatomical}</span>}</p>
            {mode.kind === 'heat' ? (
              <p className="bm-does">
                <i className={`dot ${load?.level ?? 'fresh'}`} />
                {load ? `${LEVEL_LABEL[load.level]} · last worked ${relative(load.lastWorkedAt)}` : 'Fresh · not worked in the last 7 days'}
              </p>
            ) : <p className="bm-does">{m.does}</p>}
          </>
        ) : (
          <p className="bm-does">
            {mode.kind === 'select'
              ? (st === 'none' ? `Tap the muscles you worked, or Done for the whole ${name.toLowerCase()}.` : 'Tap a muscle to see what it does.')
              : 'Tap a muscle to see its load.'}
          </p>
        )}
      </div>
      <div className="bm-chips" role="group" aria-label={`${name} muscles`}>
        {mode.kind === 'select' && list.length > 1 && (
          <button type="button" className="bm-chip whole" aria-pressed={st === 'whole'} onClick={() => onWhole(g)} data-testid="chip-whole">
            <Check on={st === 'whole'} />Whole {name.toLowerCase()}
          </button>
        )}
        {list.map((x) => {
          const on = mode.kind === 'select' ? mode.selection.specific.has(x.id) || (list.length === 1 && st === 'whole') : false;
          const level = mode.kind === 'heat' ? mode.loads.get(x.id)?.level ?? 'fresh' : null;
          return (
            <button key={x.id} type="button" className={`bm-chip${st === 'whole' && list.length > 1 ? ' included' : ''}${picked === x.id ? ' picked' : ''}`}
              aria-pressed={mode.kind === 'select' ? on : undefined} data-testid={`chip-${x.id}`}
              aria-label={level ? `${x.name}, ${LEVEL_LABEL[level].toLowerCase()}` : undefined}
              onClick={() => onPick(x.id)}>
              {level ? <i className={`dot ${level}`} /> : <Check on={on} />}{x.name}
            </button>
          );
        })}
      </div>
      {onDone && (
        <button type="button" className={`btn ${mode.kind === 'select' ? 'primary' : 'secondary'} bm-done`} onClick={onDone} data-testid="bm-done">
          {mode.kind === 'select' ? 'Done' : 'Back to body'}
        </button>
      )}
    </section>
  );
});

function Check({ on }: { on: boolean }) {
  return (
    <svg className="bm-check" viewBox="0 0 16 16" aria-hidden="true">
      {on ? <path d="M3.5 8.5l3 3 6-7" /> : <circle cx="8" cy="8" r="5.5" />}
    </svg>
  );
}

/** What is selected, as chips in taxonomy order; tapping one zooms into its group. */
function SelectionChips({ selection, onOpen, current }: { selection: Selection; onOpen: (g: string) => void; current: string | null }) {
  const chips = groups.flatMap((g) => {
    const st = groupState(selection, g.id);
    if (st === 'whole') return [{ key: g.id, group: g.id, label: g.name }];
    if (st === 'some') return musclesIn(g.id).filter((m) => selection.specific.has(m.id)).map((m) => ({ key: m.id, group: g.id, label: m.name }));
    return [];
  });
  return (
    <div className="bm-summary" data-testid="selection-summary" aria-label="Selected" role="group">
      {chips.length === 0
        ? <span className="caption">Nothing selected yet. Tap a muscle group to start.</span>
        : chips.map((c) => (
          <button key={c.key} type="button" className={`bm-sel${current === c.group ? ' current' : ''}`} onClick={() => onOpen(c.group)}
            aria-label={`${c.label}, change`}>{c.label}</button>
        ))}
    </div>
  );
}

export function HeatLegend() {
  return (
    <div className="legend caption">
      {(Object.keys(HEAT) as LoadLevel[]).map((l) => (
        <span key={l}><i style={{ background: l === 'fresh' ? 'var(--muscle-hi)' : HEAT[l] }} />{LEVEL_LABEL[l]}</span>
      ))}
    </div>
  );
}
